import { cn } from "@/lib/utils";
import { useEffect, useMemo, useRef, useState } from "react";

const EVENT_COLORS = { Vehicle: "bg-blue-500", Person: "bg-green-500", Animal: "bg-orange-500", Others: "bg-red-500" };
const EVENT_LABELS = { Vehicle: "Vehicul", Person: "Persoană", Animal: "Animal", Others: "Altele" };

export function FilmTimeline({ playlist, activeIndex, onSelect, timezone, events, onEventClick }) {
  const [activeCategories, setActiveCategories] = useState(new Set(["Vehicle", "Person", "Animal", "Others"]));

  const { minMs, maxMs, hasTimes } = useMemo(() => {
    const stamps = playlist.map((p) => p.startedAtMs).filter((x) => typeof x === "number");
    if (stamps.length === 0) return { minMs: 0, maxMs: 0, hasTimes: false };
    const min = Math.min(...stamps);
    const last = playlist[playlist.length - 1];
    const max = Math.max(...stamps) + (last.durationSec || 60) * 1000;
    return { minMs: min, maxMs: max, hasTimes: true };
  }, [playlist]);

  const tickHours = useMemo(() => {
    if (!hasTimes) return [];
    const out = [];
    const startDate = new Date(minMs), endDate = new Date(maxMs);
    const cursor = new Date(startDate);
    cursor.setMinutes(0, 0, 0);
    if (cursor.getTime() < startDate.getTime()) cursor.setHours(cursor.getHours() + 1);
    while (cursor.getTime() <= endDate.getTime()) {
      out.push({ ms: cursor.getTime(), label: cursor.toLocaleTimeString("ro-RO", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: timezone }) });
      cursor.setHours(cursor.getHours() + 1);
    }
    return out;
  }, [minMs, maxMs, hasTimes, timezone]);

  const filteredEvents = useMemo(() => (events || []).filter((ev) => activeCategories.has(ev.category)), [events, activeCategories]);
  const categoryCounts = useMemo(() => {
    const counts = {};
    for (const ev of events || []) counts[ev.category] = (counts[ev.category] || 0) + 1;
    return counts;
  }, [events]);

  const pct = (ms) => (!hasTimes || maxMs <= minMs ? 0 : ((ms - minMs) / (maxMs - minMs)) * 100);
  const toggleCategory = (cat) => setActiveCategories((prev) => {
    const next = new Set(prev);
    next.has(cat) ? next.delete(cat) : next.add(cat);
    return next;
  });

  const scrollerRef = useRef(null);
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const activeEl = el.querySelector(`[data-clip-index="${activeIndex}"]`);
    if (activeEl) activeEl.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
  }, [activeIndex]);

  const [hoveredEvent, setHoveredEvent] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const timelineRef = useRef(null);

  if (playlist.length === 0) return null;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-[11px] text-muted-foreground px-1">
        <span>
          Linie temporală · {playlist.length} clipuri
          {hasTimes && <>{" · "}{new Date(minMs).toLocaleTimeString("ro-RO", { hour: "2-digit", minute: "2-digit", timeZone: timezone })}{" – "}{new Date(maxMs).toLocaleTimeString("ro-RO", { hour: "2-digit", minute: "2-digit", timeZone: timezone })}</>}
        </span>
        <span className="text-[10px] hidden sm:inline">Click pe un marker pentru a sări la ora respectivă</span>
      </div>

      {events && events.length > 0 && (
        <div className="flex items-center gap-1.5 px-1 flex-wrap">
          <span className="text-[10px] text-muted-foreground mr-1">Filtrare markere:</span>
          {["Vehicle", "Person", "Animal", "Others"].map((cat) => {
            const count = categoryCounts[cat] || 0;
            if (count === 0) return null;
            const isActive = activeCategories.has(cat);
            return (
              <button key={cat} onClick={() => toggleCategory(cat)}
                className={cn("inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border transition-all",
                  isActive ? "border-foreground/30 bg-foreground/5 text-foreground" : "border-muted bg-muted/30 text-muted-foreground/50 line-through")}>
                <span className={cn("w-2 h-2 rounded-full", EVENT_COLORS[cat], !isActive && "opacity-30")} />
                {EVENT_LABELS[cat]} ({count})
              </button>
            );
          })}
        </div>
      )}

      <div ref={scrollerRef} className="relative overflow-x-auto rounded-lg border bg-muted/30">
        <div ref={timelineRef} className="relative h-24" style={{ minWidth: `${Math.max(playlist.length * 36, 600)}px` }}>
          {hasTimes && tickHours.map((t, i) => (
            <div key={i} className="absolute top-0 bottom-0 border-l border-border/60 pointer-events-none" style={{ left: `${pct(t.ms)}%` }}>
              <span className="absolute top-0.5 left-1 text-[10px] text-muted-foreground/80 font-medium">{t.label}</span>
            </div>
          ))}
          {playlist.map((c, i) => {
            const left = hasTimes && c.startedAtMs ? pct(c.startedAtMs) : (i / Math.max(playlist.length, 1)) * 100;
            const widthPct = hasTimes && maxMs > minMs ? Math.max(0.35, (c.durationSec * 1000) / (maxMs - minMs) * 100) : 100 / playlist.length;
            const isActive = i === activeIndex;
            const time = hasTimes && c.startedAtMs ? new Date(c.startedAtMs).toLocaleTimeString("ro-RO", { hour: "2-digit", minute: "2-digit", timeZone: timezone }) : `#${i + 1}`;
            return (
              <button key={i} data-clip-index={i} onClick={() => onSelect(i)} title={`${time} · ${c.durationSec || "?"}s · ${c.name}`}
                className={cn("absolute top-6 bottom-6 rounded transition-all hover:ring-2 hover:ring-primary hover:z-10",
                  isActive ? "bg-primary ring-2 ring-primary z-20" : "bg-primary/40 hover:bg-primary/70")}
                style={{ left: `${left}%`, width: `max(6px, ${widthPct}%)` }}>
                {isActive && <div className="absolute -top-5 left-1/2 -translate-x-1/2 whitespace-nowrap text-[10px] font-medium text-primary px-1.5 py-0.5 rounded bg-primary/10 border border-primary/30">{time}</div>}
              </button>
            );
          })}
          {hasTimes && filteredEvents.map((ev) => (
            <button key={`ev-${ev.id}`} onClick={() => onEventClick?.(ev)}
              onMouseEnter={(e) => { setHoveredEvent(ev); const rect = timelineRef.current?.getBoundingClientRect(); if (rect) setTooltipPos({ x: e.clientX - rect.left, y: e.clientY - rect.top }); }}
              onMouseLeave={() => setHoveredEvent(null)}
              className={cn("absolute bottom-7 w-2.5 h-2.5 rounded-full border-2 border-background shadow-sm z-30 transition-transform hover:scale-150", EVENT_COLORS[ev.category] || "bg-gray-500")}
              style={{ left: `${pct(ev.startedAtMs)}%`, transform: "translateX(-50%)" }} />
          ))}
          {hoveredEvent && (
            <div className="absolute z-50 pointer-events-none bg-popover text-popover-foreground border rounded-md shadow-lg px-2.5 py-1.5 text-[11px] whitespace-nowrap"
              style={{ left: `${tooltipPos.x}px`, top: `${tooltipPos.y - 45}px`, transform: "translateX(-50%)" }}>
              <div className="flex items-center gap-1.5">
                <span className={cn("w-2 h-2 rounded-full", EVENT_COLORS[hoveredEvent.category] || "bg-gray-500")} />
                <span className="font-medium">{EVENT_LABELS[hoveredEvent.category] || hoveredEvent.category}</span>
                <span className="text-muted-foreground">·</span>
                <span>{new Date(hoveredEvent.startedAtMs).toLocaleTimeString("ro-RO", { hour: "2-digit", minute: "2-digit", second: "2-digit", timeZone: timezone })}</span>
              </div>
              {hoveredEvent.notes && <div className="text-muted-foreground mt-0.5 text-[10px]">{hoveredEvent.notes}</div>}
            </div>
          )}
          <div className="absolute bottom-0 left-0 right-0 text-[10px] text-muted-foreground/70 text-center pb-0.5">
            {hasTimes ? "Pozițiile reflectă ora reală a fiecărui clip" : "Distribuție uniformă"}
          </div>
        </div>
      </div>
    </div>
  );
}
